import React from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import { Check, Copy, RefreshCw } from "lucide-react";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import useWhatsappQr from "./use-whatsapp-qr.hook";

const PAIRING_STEPS = [
  "Open WhatsApp on your phone",
  "Go to Settings → Linked Devices",
  "Tap Link a Device",
  "Tap Link with phone number instead",
  "Enter the pairing code shown",
];

const qrSteps = (businessApp: boolean) => [
  businessApp
    ? "Open WhatsApp Business on your phone"
    : "Open WhatsApp on your phone",
  "Tap Linked Devices",
  "Tap Link a device",
  "Scan this QR",
];

const DESCRIPTION =
  "Only your business SIM account is connected. Personal WhatsApp is never accessed.";

type Variant = "desktop" | "mobile";

const StepRow = ({
  index,
  text,
  variant = "desktop",
}: {
  index: number;
  text: string;
  variant?: Variant;
}) => {
  const theme = useTheme();
  const isMobileVariant = variant === "mobile";
  return (
    <Box
      sx={{ display: "flex", alignItems: "center", gap: theme.nawy.spacing.sm }}
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          backgroundColor: isMobileVariant
            ? theme.nawy.colors.primitives.navy[400]
            : theme.nawy.colors.primitives.navy[100],
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 500,
            lineHeight: 1,
            color: isMobileVariant
              ? "#fff"
              : theme.nawy.colors.primitives.navy[700],
          }}
        >
          {index}
        </Typography>
      </Box>
      <Typography
        sx={{
          ...theme.nawy.typography.english.variants.body.sm.regular,
          color: theme.nawy.colors.semantic.text.primary,
          fontSize: 14,
          lineHeight: 1.4,
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};

interface RefreshCodeButtonProps {
  isFetching: boolean;
  canRefresh: boolean;
  cooldownSeconds: number;
  onRefresh: () => void;
}

const refreshLabel = ({
  isFetching,
  canRefresh,
  cooldownSeconds,
}: Omit<RefreshCodeButtonProps, "onRefresh">) => {
  if (isFetching) return "Refreshing…";
  // Never show a countdown on a button that can be pressed, as it can after a
  // failed request.
  if (canRefresh) return "Refresh code";
  return cooldownSeconds > 0 ? `Refresh in ${cooldownSeconds}s` : "Refresh code";
};

/**
 * Single entry point for requesting a new code. Stays disabled for the whole
 * cooldown and shows the remaining seconds so the wait is visible.
 */
const RefreshCodeButton = ({
  isFetching,
  canRefresh,
  cooldownSeconds,
  onRefresh,
}: RefreshCodeButtonProps) => {
  const theme = useTheme();

  const label = refreshLabel({ isFetching, canRefresh, cooldownSeconds });

  return (
    <Button
      onClick={onRefresh}
      disabled={!canRefresh}
      startIcon={
        isFetching ? (
          <CircularProgress size={14} sx={{ color: "inherit" }} />
        ) : (
          <RefreshCw size={14} />
        )
      }
      sx={{
        textTransform: "none",
        borderRadius: theme.nawy.radius.sm,
        px: theme.nawy.spacing.sm,
        minWidth: 148,
        ...theme.nawy.typography.english.variants.body.sm.semibold,
        color: theme.nawy.colors.semantic.text.accent,
        "&.Mui-disabled": {
          color: theme.nawy.colors.semantic.text.tertiary,
        },
      }}
    >
      {label}
    </Button>
  );
};

interface WhatsappQrCodeDisplayProps {
  qrCodeSrc: string | undefined;
  isFetching: boolean;
  isError: boolean;
  variant?: Variant;
}

const WhatsappQrCodeDisplay = ({
  qrCodeSrc,
  isFetching,
  isError,
  variant = "desktop",
}: WhatsappQrCodeDisplayProps) => {
  const theme = useTheme();
  const isMobileVariant = variant === "mobile";
  const outerSize = isMobileVariant ? 180 : 160;
  const innerSize = isMobileVariant ? 172 : 152;

  return (
    <Box
      sx={{
        width: outerSize,
        height: outerSize,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#fff",
        borderRadius: "8px",
        overflow: "hidden",
      }}
    >
      {!qrCodeSrc && !isError && (
        <Skeleton
          variant="rectangular"
          sx={{ borderRadius: "6px", width: innerSize, height: innerSize }}
        />
      )}

      {isError && !isFetching && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: theme.nawy.spacing.xs,
            padding: theme.nawy.spacing.sm,
          }}
        >
          <Typography
            sx={{
              ...theme.nawy.typography.english.variants.body.sm.regular,
              color: theme.nawy.colors.semantic.indicator.red,
              textAlign: "center",
              fontSize: 12,
            }}
          >
            Failed to load
          </Typography>
        </Box>
      )}

      {qrCodeSrc && (
        <Box
          sx={{
            position: "relative",
            width: innerSize,
            height: innerSize,
            flexShrink: 0,
          }}
        >
          <img
            src={qrCodeSrc}
            alt="WhatsApp QR Code"
            style={{ display: "block", width: "100%", height: "100%" }}
          />
          {isFetching && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "rgba(255,255,255,0.6)",
              }}
            >
              <CircularProgress
                size={24}
                sx={{ color: theme.nawy.colors.semantic.text.secondary }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export interface WhatsappQrContentProps {
  /** When false, no code is requested and the passport is not polled. */
  enabled?: boolean;
  /** First QR step wording; false switches it to plain WhatsApp. */
  businessApp?: boolean;
  /** Grow to fill the parent (modal) instead of using a fixed minimum height (overlay). */
  fillHeight?: boolean;
  /** Called once the passport reports the instance as linked. */
  onConnected?: () => void;
}

/**
 * Shared body of both relink surfaces: the QR view, the pairing-code view and
 * the refresh button that requests a new code.
 */
const WhatsappQrContent = ({
  enabled = true,
  businessApp = true,
  fillHeight = false,
  onConnected,
}: WhatsappQrContentProps) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();
  const {
    qrCodeSrc,
    pairingCode,
    isFetching,
    isError,
    isConnected,
    refresh,
    canRefresh,
    cooldownSeconds,
  } = useWhatsappQr({ enabled });

  const [showPairingCode, setShowPairingCode] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const notifiedRef = React.useRef(false);
  React.useEffect(() => {
    if (!isConnected || notifiedRef.current) return;
    notifiedRef.current = true;
    onConnected?.();
  }, [isConnected, onConnected]);

  const handleCopyPairingCode = async () => {
    if (!pairingCode) return;
    try {
      await navigator.clipboard.writeText(pairingCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* noop */
    }
  };

  const refreshButton = (
    <RefreshCodeButton
      isFetching={isFetching}
      canRefresh={canRefresh}
      cooldownSeconds={cooldownSeconds}
      onRefresh={refresh}
    />
  );

  const panelSizing = fillHeight ? { flex: 1 } : { minHeight: 340 };

  const qrColumn = (variant: Variant) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: "6px",
        flexShrink: 0,
        ...(variant === "mobile" ? { width: "100%" } : {}),
      }}
    >
      <WhatsappQrCodeDisplay
        qrCodeSrc={qrCodeSrc}
        isFetching={isFetching}
        isError={isError}
        variant={variant}
      />
      {refreshButton}
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: theme.nawy.spacing.md,
        ...(fillHeight ? { flex: 1, minHeight: 0 } : {}),
      }}
    >
      <Typography
        sx={{
          ...theme.nawy.typography.english.variants.body.sm.regular,
          color: theme.nawy.colors.semantic.text.secondary,
          fontSize: 14,
          lineHeight: 1.5,
        }}
      >
        {DESCRIPTION}
      </Typography>

      {!showPairingCode ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: theme.nawy.spacing.md,
            p: theme.nawy.spacing.md,
            backgroundColor: theme.nawy.colors.primitives.white[200],
            borderRadius: "12px",
            alignItems: "center",
            ...panelSizing,
          }}
        >
          {isMobile && qrColumn("mobile")}

          <Box
            sx={{
              flex: isMobile ? "none" : 1,
              width: isMobile ? "100%" : "auto",
              minWidth: 0,
              display: "flex",
              flexDirection: "column",
              gap: theme.nawy.spacing.sm,
            }}
          >
            {qrSteps(businessApp).map((step, i) => (
              <StepRow
                key={step}
                index={i + 1}
                text={step}
                variant={isMobile ? "mobile" : "desktop"}
              />
            ))}
          </Box>

          {!isMobile && qrColumn("desktop")}
        </Box>
      ) : (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: theme.nawy.spacing.md,
            p: theme.nawy.spacing.md,
            backgroundColor: theme.nawy.colors.primitives.white[200],
            borderRadius: "12px",
            justifyContent: "center",
            ...panelSizing,
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "4px",
              py: theme.nawy.spacing.sm,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: theme.nawy.spacing.sm,
              }}
            >
              <Typography
                sx={{
                  fontFamily: "monospace",
                  fontSize: 32,
                  fontWeight: 700,
                  letterSpacing: "0.25em",
                  color: theme.nawy.colors.semantic.text.primary,
                  userSelect: "all",
                }}
              >
                {pairingCode}
              </Typography>
              <IconButton
                size="small"
                onClick={handleCopyPairingCode}
                aria-label="Copy pairing code"
                sx={{
                  color: copied
                    ? theme.nawy.colors.semantic.indicator.green
                    : theme.nawy.colors.semantic.text.secondary,
                }}
              >
                {copied ? <Check size={18} /> : <Copy size={18} />}
              </IconButton>
              {copied && (
                <Typography
                  sx={{
                    ...theme.nawy.typography.english.variants.body.xs.regular,
                    color: theme.nawy.colors.semantic.indicator.green,
                  }}
                >
                  Copied!
                </Typography>
              )}
            </Box>
            {refreshButton}
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: theme.nawy.spacing.sm,
            }}
          >
            {PAIRING_STEPS.map((step, i) => (
              <StepRow
                key={step}
                index={i + 1}
                text={step}
                variant={isMobile ? "mobile" : "desktop"}
              />
            ))}
          </Box>
        </Box>
      )}

      {pairingCode && (
        <Typography
          sx={{
            ...theme.nawy.typography.english.variants.body.sm.regular,
            color: theme.nawy.colors.semantic.text.secondary,
            fontSize: 14,
          }}
        >
          {showPairingCode ? (
            <>
              Have your QR scanner ready?{" "}
              <Box
                component="button"
                onClick={() => setShowPairingCode(false)}
                sx={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  p: 0,
                  color: theme.nawy.colors.semantic.text.accent,
                  textDecoration: "underline",
                  font: "inherit",
                  "&:hover": { opacity: 0.8 },
                }}
              >
                Use QR code instead
              </Box>
            </>
          ) : (
            <>
              On mobile & can&apos;t scan?{" "}
              <Box
                component="button"
                onClick={() => setShowPairingCode(true)}
                sx={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  p: 0,
                  color: theme.nawy.colors.semantic.text.accent,
                  textDecoration: "underline",
                  font: "inherit",
                  "&:hover": { opacity: 0.8 },
                }}
              >
                Tap here to connect
              </Box>
            </>
          )}
        </Typography>
      )}
    </Box>
  );
};

export default WhatsappQrContent;
